import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert as MuiAlert,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore,
  FamilyRestroom,
  MedicalServices,
  Favorite,
  EventNote,
  Psychology,
  History,
  Warning,
  CheckCircle,
  AccessTime,
  PersonOutline,
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const CATEGORY_CONFIG = {
  family: { label: 'Familia', icon: <FamilyRestroom sx={{ fontSize: 18 }} />, color: '#7C3AED', bg: '#F5F3FF' },
  medical: { label: 'Salud', icon: <MedicalServices sx={{ fontSize: 18 }} />, color: '#DC2626', bg: '#FEF2F2' },
  preferences: { label: 'Preferencias', icon: <Favorite sx={{ fontSize: 18 }} />, color: '#2563EB', bg: '#EFF6FF' },
  events: { label: 'Eventos', icon: <EventNote sx={{ fontSize: 18 }} />, color: '#D97706', bg: '#FEF3C7' },
  daily_life: { label: 'Vida diaria', icon: <Psychology sx={{ fontSize: 18 }} />, color: '#059669', bg: '#F0FDF4' },
};

export const FamilyPatientInfo = () => {
  const [linkedPatients, setLinkedPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    fetchLinkedPatients();
  }, []);

  const fetchLinkedPatients = async () => {
    try {
      const data = await apiRequest('/patients/my-patients');
      const patients = data.patients || [];
      setLinkedPatients(patients);
      if (patients.length > 0) {
        loadPatientProfile(patients[0].id);
      }
    } catch (e) {
      console.error('Error fetching linked patients:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientProfile = async (patientId) => {
    setSelectedPatientId(patientId);
    setProfileLoading(true);
    setTab(0);
    try {
      const data = await apiRequest(`/patients/${patientId}/family-view`);
      setProfile(data.patient);
    } catch (e) {
      console.error('Error loading patient info:', e);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const groupMemories = (memories) => {
    const grouped = {};
    for (const m of memories || []) {
      if (!grouped[m.category]) grouped[m.category] = [];
      grouped[m.category].push(m);
    }
    return grouped;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (linkedPatients.length === 0) {
    return (
      <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
        <PersonOutline sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
        <Typography variant="h5" color="text.secondary" fontWeight={500}>
          No hay pacientes vinculados
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contactá al centro para vincular a tu familiar.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Patient selector - if multiple linked patients */}
      {linkedPatients.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {linkedPatients.map((p) => (
            <Chip
              key={p.id}
              label={`${p.first_name} ${p.last_name}`}
              onClick={() => loadPatientProfile(p.id)}
              color={selectedPatientId === p.id ? 'primary' : 'default'}
              variant={selectedPatientId === p.id ? 'filled' : 'outlined'}
              avatar={<Avatar sx={{ bgcolor: selectedPatientId === p.id ? 'primary.dark' : '#EFF6FF' }}>{p.first_name?.[0]}</Avatar>}
            />
          ))}
        </Stack>
      )}

      {profileLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : profile ? (
        <>
          {/* Patient header */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'white', mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600, fontSize: '1rem' }}>
                {(profile.first_name?.[0] || '').toUpperCase()}{(profile.last_name?.[0] || '').toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4">{profile.first_name} {profile.last_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.total_calls} llamadas registradas
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Tabs */}
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
                <Tab label="Recuerdos" icon={<Psychology sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 44, fontSize: '0.8rem' }} />
                <Tab label="Salud" icon={<MedicalServices sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 44, fontSize: '0.8rem' }} />
                {profile.recent_calls && (
                  <Tab label="Llamadas" icon={<History sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 44, fontSize: '0.8rem' }} />
                )}
                <Tab label="Alertas" icon={<Warning sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 44, fontSize: '0.8rem' }} />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Memories */}
              {tab === 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Información que el asistente de IA recuerda de tu familiar, extraída de sus conversaciones.
                  </Typography>
                  {profile.memories.length === 0 ? (
                    <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                      <Psychology sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">Aún no hay recuerdos guardados.</Typography>
                    </Paper>
                  ) : (
                    Object.entries(groupMemories(profile.memories)).map(([category, items]) => {
                      const config = CATEGORY_CONFIG[category] || { label: category, icon: <Psychology sx={{ fontSize: 18 }} />, color: '#64748B', bg: '#F8FAFC' };
                      return (
                        <Accordion key={category} defaultExpanded elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5, '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: config.bg, color: config.color }}>{config.icon}</Avatar>
                              <Typography variant="h6">{config.label}</Typography>
                              <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              {items.map((mem, i) => (
                                <Box key={i} sx={{ py: 0.75, px: 1, borderRadius: 1, '&:hover': { bgcolor: '#F8FAFC' } }}>
                                  <Typography variant="body2" fontWeight={600} color="text.primary">{mem.key}</Typography>
                                  <Typography variant="body2" color="text.secondary">{mem.value}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>
              )}

              {/* Medical */}
              {tab === 1 && (
                <Stack spacing={2}>
                  {[
                    { label: 'Fecha de nacimiento', value: profile.medical.date_of_birth ? formatDate(profile.medical.date_of_birth) : null },
                    { label: 'Grupo sanguíneo', value: profile.medical.blood_type },
                    { label: 'Contacto de emergencia', value: profile.medical.emergency_contact_name ? `${profile.medical.emergency_contact_name} — ${profile.medical.emergency_contact_phone || ''}` : null },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Typography variant="caption" fontWeight={600}>{label}</Typography>
                      <Typography variant="body1">{value || '—'}</Typography>
                    </Box>
                  ))}
                  {[
                    { label: 'Alergias', items: profile.medical.allergies, color: '#DC2626', bg: '#FEF2F2' },
                    { label: 'Condiciones crónicas', items: profile.medical.chronic_conditions, color: '#D97706', bg: '#FEF3C7' },
                    { label: 'Medicación actual', items: profile.medical.current_medications, color: '#2563EB', bg: '#EFF6FF' },
                  ].map(({ label, items, color, bg }) => (
                    <Box key={label}>
                      <Typography variant="caption" fontWeight={600}>{label}</Typography>
                      {items && items.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                          {items.map((item, i) => <Chip key={i} label={item} size="small" sx={{ bgcolor: bg, color, fontWeight: 500 }} />)}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Sin datos</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Calls - only if available (permission-based) */}
              {tab === 2 && profile.recent_calls && (
                <Box>
                  {profile.recent_calls.length === 0 ? (
                    <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                      <History sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">Sin llamadas registradas</Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1.5}>
                      {profile.recent_calls.map((call) => (
                        <Paper key={call.id} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1 }}>
                              <Chip
                                label={call.status === 'completed' ? 'Completada' : call.status}
                                size="small"
                                color={call.status === 'completed' ? 'success' : 'default'}
                                sx={{ height: 22, fontSize: '0.7rem', mb: 0.5 }}
                              />
                              {call.summary && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-line' }}>{call.summary}</Typography>
                              )}
                            </Box>
                            <Stack alignItems="flex-end" sx={{ ml: 2 }}>
                              <Typography variant="caption">{formatDateTime(call.started_at)}</Typography>
                              {call.duration_seconds && (
                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccessTime sx={{ fontSize: 12 }} /> {Math.floor(call.duration_seconds / 60)}m
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* Alerts */}
              {((profile.recent_calls && tab === 3) || (!profile.recent_calls && tab === 2)) && (
                <Box>
                  {profile.alerts.length === 0 ? (
                    <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
                      <CheckCircle sx={{ fontSize: 48, color: '#10B981', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">Sin alertas. Todo parece estar bien.</Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1.5}>
                      {profile.alerts.map((alert) => (
                        <MuiAlert key={alert.id} severity={alert.severity === 'urgent' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight={600}>{alert.alert_type}</Typography>
                          <Typography variant="body2">{alert.content}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatDateTime(alert.created_at)}</Typography>
                        </MuiAlert>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </>
      ) : (
        <Paper elevation={0} sx={{ textAlign: 'center', py: 6, border: '1px dashed', borderColor: '#E2E8F0' }}>
          <Typography variant="body1" color="text.secondary">Error al cargar la información</Typography>
        </Paper>
      )}
    </Box>
  );
};
